/**
 * R3 · 改图能力卡条(设计稿 r2-remix-studio v2)。
 *
 * 卡不是表单,是**给指令打底**:点一张 → 把骨架写进下面的「改图指令」框;
 * 再点一次(或改动指令)不做任何锁定 —— 指令框永远是唯一真相。
 * 不点卡直接写 = 原「图改图」的自由路径,一点没少。
 *
 * ★2026-07-31:原注释写"并推荐保真档" —— 那个档(`RemixRecipe.fidelity`)从来没渲染过、
 *   也没发给过后端,已随任务 20 删除。第 4 张 `vision` 卡是**另一条路**:它不改这张图,
 *   而是读懂它再从零画一张(点它会把模式切回文字),所以卡面要说清"不是在原图上改"。
 */
import { Loader2 } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { REMIX_RECIPES, type RemixRecipe } from "../../../model/free-create/remixRecipes";

interface Props {
  /** 当前指令来自哪张卡(仅作卡面高亮;用户改了指令也不清除 —— 那是"来源",不是"状态")。 */
  activeKey: string | null;
  onPick: (recipe: RemixRecipe) => void;
  disabled?: boolean;
  /** vision 卡正在读参考图(只锁那一张,其余卡照常可点)。 */
  describing?: boolean;
}

export function RemixRecipePicker({ activeKey, onPick, disabled, describing }: Props) {
  return (
    <div className="space-y-2" data-remix-picker>
      <div className="text-[12.5px] font-medium text-muted-foreground">
        要改什么?点一张卡给指令打底,或跳过卡直接写 ——
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {REMIX_RECIPES.map((r) => {
          const active = activeKey === r.key;
          return (
            <button
              key={r.key}
              type="button"
              data-remix-recipe={r.key}
              aria-pressed={active}
              disabled={disabled || (r.vision && describing)}
              onClick={() => onPick(r)}
              className={cn(
                "flex flex-col gap-1 rounded-[calc(var(--radius)*1.2)] border p-3 text-left transition-colors fx-press",
                active
                  ? "border-primary bg-primary/5"
                  : "border-border/60 bg-card/40 hover:border-primary/50",
                disabled && "pointer-events-none opacity-60"
              )}
            >
              <b className="flex items-center gap-1.5 text-[14px] font-semibold">
                {r.vision && describing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {r.title}
              </b>
              <span className="text-[12.5px] leading-relaxed text-muted-foreground">{r.desc}</span>
              {r.vision && describing && (
                <span className="mt-0.5 text-2xs font-medium text-primary">正在看这张图…</span>
              )}
              {active && !(r.vision && describing) && (
                <span className="mt-0.5 text-2xs font-medium text-primary">
                  {r.vision ? "已写好画面描述 · 走文生图" : "已把指令写进下面 · 每个字都能改"}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
